<template>
  <div class="options-list-container">
    <!-- Empty State -->
    <div v-if="!options || options.length === 0" class="text-center my-4">
      <p class="text-muted">⚠ No options found for this group.</p>
      <CButton
        color="success"
        size="sm"
        @click="$emit('edit-option', null, optionGroupId)"
      >
        <CIcon name="cil-plus" /> Add First Option
      </CButton>
    </div>

    <!-- Options Table -->
    <div v-else>
      <div class="d-flex justify-content-between mb-3">
        <CButton
          color="primary"
          size="sm"
          @click="$emit('edit-option', null, optionGroupId)"
        >
          <CIcon name="cil-plus" /> <strong>Add</strong> Option
        </CButton>

        <div class="d-flex flex-gap-2">
          <!-- Bulk Actions -->
          <v-select
            v-model="bulkAction"
            :options="bulkActionOptions"
            :reduce="(option) => option.value"
            :searchable="false"
            placeholder="Bulk Actions..."
            custom
            class="mb-0"
            style="width: 180px"
          />

          <CButton color="dark" size="sm" :disabled="!selectedOptions.length || !bulkAction" @click="executeBulkAction">
            Apply
          </CButton>
        </div>
      </div>

      <CDataTable
        :items="sortedOptions"
        :fields="fields"
        hover
        striped
        sorter
        responsive
        class="options-table"
      >
        <!-- Selection Checkbox -->
        <template #select="{ item }">
          <td>
            <CInputCheckbox
              v-model="item.checked"
              :value="item.id"
              @update:checked="updateSelectedOptions(item.id, $event)"
              custom
            />
          </td>
        </template>

        <!-- Display Order -->
        <template #display_order="{ item }">
          <td class="move-item text-center">
            <div class="d-flex align-items-center justify-content-center flex-gap-3">
              <CIcon
                name="cil-move"
                :data-id="item.id"
                class="align-bottom mr-2 handle"
                title="Drag to reorder"
              />
              <span>{{ item.display_order }}</span>
            </div>
          </td>
        </template>

        <!-- Name -->
        <template #name="{ item }">
          <td>
            <div class="d-flex align-items-center">
              <span :class="{ 'text-muted': item.sold_out }">{{ item.name }}</span>
              <CBadge v-if="item.is_default" color="success" class="ml-2">Default</CBadge>
            </div>
            <small v-if="item.description" class="text-muted">{{ item.description }}</small>
          </td>
        </template>

        <!-- Price -->
        <template #price="{item}">
          <td>
            <CInput
              class="mb-0"
              :value.sync="item.price"
              placeholder="0.00"
              type="number"
              step="0.10"
              pattern="^\d+(?:\.\d{1,2})?$"
              onchange="(function(el){el.value=parseFloat(el.value).toFixed(2);})(this)"
              :disabled="item.sold_out"
              @input="value => queueUpdate(item, value, 'price')"
            >
              <template #prepend-content><CIcon name="cil-euro" /></template>
            </CInput>
          </td>
        </template>

        <!-- Takeout Price -->
        <template v-if="usetoprice" #toPrice="{item}">
          <td>
            <CInput
              class="mb-0"
              :value.sync="item.toPrice"
              placeholder="0.00"
              type="number"
              step="0.10"
              pattern="^\d+(?:\.\d{1,2})?$"
              onchange="(function(el){el.value=parseFloat(el.value).toFixed(2);})(this)"
              :disabled="item.sold_out || samepricetoprice"
              @input="value => queueUpdate(item, value, 'toPrice')"
            >
              <template #prepend-content><CIcon name="cil-euro" /></template>
            </CInput>
          </td>
        </template>

        <!-- Sold Out -->
        <template #sold_out="{ item }">
          <td>
            <CSwitch
              class="mt-1"
              :checked.sync="item.sold_out"
              labelOn="YES"
              labelOff="NO"
              color="danger"
              shape="pill"
              @update:checked="value => queueUpdate(item, value, 'sold_out')"
            />
          </td>
        </template>

        <!-- Actions -->
        <template #actions="{ item }">
          <td class="options-actions text-right">
            <CButton
              color="info"
              size="sm"
              shape="pill"
              variant="ghost"
              @click="$emit('edit-option', item, optionGroupId)"
            >
              <CIcon name="cil-pencil" />
            </CButton>

            <CButton
              color="danger"
              size="sm"
              shape="pill"
              variant="ghost"
              @click="$emit('delete-option', item.id, optionGroupId)"
            >
              <CIcon name="cil-trash" />
            </CButton>
          </td>
        </template>
      </CDataTable>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import _debounce from 'lodash/debounce';
import Sortable from 'sortablejs';

export default {
  name: 'OptionsList',
  props: {
    options: { type: Array, default: () => [] },
    optionGroupId: { type: Number, required: true },
    usetoprice: { type: Boolean, default: false },
    samepricetoprice: { type: Boolean, default: false }
  },
  data() {
    return {
      selectedOptions: [],
      bulkAction: null,
      bulkActionOptions: [
        { value: 'sold_out', label: 'Sold Out' },
        { value: 'in_stock', label: 'In Stock' },
        { value: 'delete', label: 'Delete' }
      ],
      fields: [
        { key: 'select', label: '', sorter: false, _style: 'width: 48px; min-width: 48px' },
        { key: 'display_order', label: 'Order', _style: 'width: 96px; min-width: 96px' },
        { key: 'name', label: 'Name' },
        { key: 'price', label: 'Price', _style: 'width: 120px; min-width: 120px' },
        { key: 'toPrice', label: 'Takeout Price', _style: 'width: 120px; min-width: 120px' },
        { key: 'sold_out', label: 'Sold Out', _style: 'width: 100px; min-width: 100px' },
        { key: 'actions', label: '', _style: 'width: 110px; min-width: 110px', sorter: false }
      ],
      sortable: null,
      restaurantId: this.$route.params.restaurant_id,

      updateQueue: [],
      updating: false,
      debouncedProcessQueue: null,
    }
  },

  computed: {
    ...mapGetters(["errors"]),

    sortedOptions() {
      return this.options
        .map(o => ({ ...o, _classes: o._classes ?? '' }))
        .sort((a, b) => a.display_order - b.display_order);
    }
  },

  created() {
    // Create a debounced version of processQueue that waits 500ms before executing
    this.debouncedProcessQueue = _debounce(this.processQueue, 500)
  },

  mounted() {
    if (!this.usetoprice) {
      this.fields = this.fields.filter((e) => e.key !== "toPrice");
    }

    this.$nextTick(() => {
      this.initSortable()
    })
  },

  watch: {
    options: {
      handler() {
        this.$nextTick(() => {
          this.initSortable()
        })
      },
      // deep: true
    }
  },

  methods: {
    handleErrors(error) {
      if (typeof this.errors === 'string') {
        this.$toast.error(this.errors);
      } else if (typeof this.errors === 'object' && Object.values(this.errors).length > 0) {
        this.$toast.error(Object.values(this.errors).join());
      } else {
        this.$toast.error(error.message);
      }
    },

    updateSelectedOptions(id, checked) {
      if (checked) {
        if (!this.selectedOptions.includes(id)) {
          this.selectedOptions.push(id)
        }
      } else {
        const index = this.selectedOptions.indexOf(id)
        if (index > -1) {
          this.selectedOptions.splice(index, 1)
        }
      }
    },

    initSortable() {
      const el = this.$el.querySelector('.options-table tbody')
      if (!el) return

      if (this.sortable) {
        this.sortable.destroy()
      }

      this.sortable = new Sortable(el, {
        handle: '.handle',
        animation: 150,
        ghostClass: 'sortable-ghost',
        chosenClass: 'sortable-chosen',
        onEnd: this.handleSortEnd
      })
    },

    async handleSortEnd(evt) {
      const { oldIndex, newIndex } = evt
      if (oldIndex === newIndex) return

      // Get the current sorted array to work with (important since we're using sortedOptions in the table)
      const optionsCopy = [...this.sortedOptions]

      // Remove the item from old position and insert at new position
      const movedItem = optionsCopy.splice(oldIndex, 1)[0]
      optionsCopy.splice(newIndex, 0, movedItem)

      // Update display_order values in the reordered array
      optionsCopy.forEach((option, index) => {
        option.display_order = index + 1
      })

      // Create the updated order array to send to backend
      const updatedOptions = optionsCopy.map(({ id, display_order }) => ({ id, display_order }))

      // Call backend to update order
      try {
        await this.$axios.post(
          this.$backend.MENU_V2.UPDATE_OPTION_GROUP_OPTION_ORDER(this.restaurantId, this.optionGroupId),
          { options: updatedOptions }
        )

        // Update the parent component's data with the properly ordered options
        this.$emit('options-reordered', optionsCopy)
      } catch (error) {
        this.handleErrors(error)
        this.$emit('refresh-data') // Refresh to revert to original order
      }
    },

    queueUpdate(option, value, field) {
      // Clear any existing entries for this option and field to avoid multiple updates
      this.updateQueue = this.updateQueue.filter(item => 
        !(item.option.id === option.id && item.field === field)
      )
      
      // Add the new update to the queue
      this.updateQueue.push({ option, value, field })

      // If we're updating price and prices are synced, update toPrice as well
      if (this.usetoprice && this.samepricetoprice && field === 'price') {
        // Clear any existing toPrice entries for this option
        this.updateQueue = this.updateQueue.filter(item => 
          !(item.option.id === option.id && item.field === 'toPrice')
        )
        this.updateQueue.push({ option, value, field: 'toPrice' })
      }

      // Apply visual feedback immediately
      this.updateItemClass(option, 'table-warning')
      
      // Use the debounced version of processQueue
      this.debouncedProcessQueue()
    },

    async processQueue() {
      if (this.updating || this.updateQueue.length === 0) return
      this.updating = true

      const { option, value, field } = this.updateQueue.shift()

      if (!option) {
        this.updating = false
        return
      }

      // Set the field value directly
      option[field] = value

      // Apply visual feedback immediately
      this.updateItemClass(option, 'table-warning disable-item')

      if (field === 'price' && option.price === '') {
        option.price = 0
      }

      if (field === 'toPrice' && option.toPrice === '') {
        option.toPrice = 0
      }

      try {
        await this.$axios.put(
          this.$backend.MENU_V2.UPDATE_OPTION_GROUP_OPTION(
            this.restaurantId,
            this.optionGroupId,
            option.id
          ),
          { [field]: option[field] }
        )
        this.updateItemClass(option, 'table-success')
        setTimeout(() => this.updateItemClass(option, ''), 1000)
      } catch (error) {
        this.updateItemClass(option, 'table-danger')
        this.handleErrors(error)
        setTimeout(() => this.updateItemClass(option, ''), 1000)
      }

      this.updating = false
      this.processQueue()
    },

    updateItemClass(item, className) {
      if (!item) return

      item._classes = className
    },

    async executeBulkAction() {
      if (!this.selectedOptions.length || !this.bulkAction) return

      switch (this.bulkAction) {
        case 'sold_out':
        case 'in_stock':
          try {
            await this.$axios.post(
              this.$backend.MENU_V2.UPDATE_OPTION_GROUP_OPTION_ACTIVE_BULK(this.restaurantId, this.optionGroupId),
              {
                option_ids: this.selectedOptions,
                sold_out: this.bulkAction === 'sold_out' ? 1 : 0
              }
            )

            this.$toast.success(`${this.selectedOptions.length} options marked as ${this.bulkAction === 'sold_out' ? 'sold out' : 'in stock'}.`)
            this.$emit('refresh-data')
          } catch (error) {
            this.handleErrors(error)
          }
          break

        case 'delete':
          this.$confirm({
            title: 'Confirm Delete',
            message: `Are you sure you want to delete ${this.selectedOptions.length} options?`,
            button: {
              no: 'Cancel',
              yes: 'Yes, Delete Them'
            },
            callback: async confirm => {
              if (confirm) {
                try {
                  await this.$axios.post(
                    this.$backend.MENU_V2.DELETE_OPTION_GROUP_OPTIONS_BULK(this.restaurantId, this.optionGroupId),
                    { option_ids: this.selectedOptions }
                  )

                  this.$toast.success(`${this.selectedOptions.length} options deleted`)
                  this.selectedOptions = []
                  this.$emit('refresh-data')
                } catch (error) {
                  this.handleErrors(error)
                }
              }
            }
          })
          break
      }

      this.bulkAction = null
    }
  }
}
</script>

<style scoped>
.options-list-container {
  padding: 0;
}

.options-table {
  border: 1px solid #ebedef;
}

.handle {
  cursor: move;
  color: #999;
  font-size: 1.1rem;
  transition: color 0.2s ease;
}

.handle:hover {
  color: #333;
  transform: scale(1.1);
}

.options-actions {
  white-space: nowrap;
}

/* Sortable styles */
.sortable-ghost {
  background-color: #f8f9fa;
  opacity: 0.8;
}

.sortable-chosen {
  background-color: #f0f7ff;
}

.move-item:hover {
  background-color: #f8f9fa;
}
</style>

<style>
.options-table .table {
  margin-bottom: 0 !important;
}
.options-table table th, .options-table table td {
  padding: 0.375rem;
  vertical-align: middle;
}
</style>
